import type { DefaultOptions } from 'up-fetch';

import { captureException } from '@sentry/react';
import { saveAs } from 'file-saver';
import { isResponseValidationError, up } from 'up-fetch';
import { z } from 'zod';

import { env } from '~/constants/env';
import { isPlainObject, isPrimitive } from '~/utils/objects';

import type { HttpError } from './errors';

import {
  FetchError,
  HttpBusinessConflictError,
  HttpClientError,
  httpErrorFromStatus,
  HttpUnprocessableContentError,
  isAbortError,
  isFetchError,
  isTimeoutError,
  shouldCaptureErrorInSentry,
} from './errors';

const defaultOptions = {
  baseUrl: `${env.API_URL}/api/v2`,
  timeout: 15_000,
  credentials: 'include',

  onError: (error, request) => {
    if (!(error instanceof Error)) return;

    // Improve readability of validation error message
    if (isResponseValidationError(error)) {
      error.message = z.prettifyError(error);
    }

    // Throws custom errors for fetch errors
    const { pathname } = new URL(request.url);

    if (isTimeoutError(error)) throw new FetchError(`Request to ${pathname} timed out`, { cause: error });
    if (isAbortError(error)) throw new FetchError(`Request to ${pathname} aborted`, { cause: error });
    if (isFetchError(error)) throw new FetchError(`Could not reach the server at ${pathname}`, { cause: error });

    if (shouldCaptureErrorInSentry(error)) captureException(error, { level: 'error' });
  },

  parseRejected: async (response): Promise<HttpError> => {
    const statusError = httpErrorFromStatus(response.status);

    if (statusError) return statusError;

    const error = await response
      .clone()
      .json()
      .catch(() => response.text())
      .then((data) => data || null);

    if (response.status === 422) return new HttpUnprocessableContentError(error.errors);
    if (response.status === 409) return new HttpBusinessConflictError(error.errorCode);

    return new HttpClientError(response.status, { cause: error });
  },

  serializeParams: (params) => {
    const queryStringComponents = Object.entries(params)
      .flatMap(([key, value]) => buildQueryStringComponent(key, value))
      .filter((queryStringComponent) => queryStringComponent !== null);

    return queryStringComponents.length > 0 ? `?${queryStringComponents.join('&')}` : '';
  },
} as const satisfies DefaultOptions<typeof fetch, unknown, unknown>;

const fetchFn: typeof fetch = (...args) => fetch(...args);

export const fetchJson = up(fetchFn, () => {
  return {
    ...defaultOptions,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
});

export const fetchFormData = up(fetchFn, () => {
  return {
    ...defaultOptions,
    headers: {
      Accept: 'application/json',
    },
  };
});

export const fetchBlob = up(fetchFn, () => {
  return {
    ...defaultOptions,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    parseResponse: async (response) => (response.status === 204 ? null : await response.blob()),
  };
});

export const downloadBlob = up(fetchFn, () => {
  return {
    ...defaultOptions,
    baseUrl: `${env.API_URL}/api/v2`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    parseResponse: async (response) => (response.status === 204 ? null : { blob: await response.blob(), response }),
    onSuccess: (data: { blob: Blob; response: Response } | null) => {
      if (!data || data.blob.size === 0) return;

      const { blob, response } = data;

      const contentDispositionHeader = response.headers.get('Content-Disposition') ?? '';
      const fileNameUtf8Regex = /filename\*=UTF-8''(?<name>[^;]+);?/i;
      const fileNameRegex = /filename="?(?<name>[^;"]+)"?;?/i;
      const fileName =
        contentDispositionHeader.match(fileNameUtf8Regex)?.groups?.name.trim() ||
        contentDispositionHeader.match(fileNameRegex)?.groups?.name.trim() ||
        'download';

      saveAs(blob, decodeURIComponent(fileName));
    },
  };
});

const buildQueryStringComponent = (key: string, value: unknown): string | null | (string | null)[] => {
  if (value === '' || value === null || value === undefined) return null;

  if (Array.isArray(value)) return value.flatMap((v, i) => buildQueryStringComponent(`${key}[${i}]`, v));

  if (isPlainObject(value)) return Object.entries(value).flatMap(([k, v]) => buildQueryStringComponent(`${key}[${k}]`, v));

  if (typeof value === 'boolean') return `${key}=${encodeURIComponent(value ? 1 : 0)}`;

  if (isPrimitive(value)) return `${key}=${encodeURIComponent(value.toString())}`;

  return null;
};
